/**
 * FetchChatMessagesUseCase
 *
 * Paginated fetch of chat messages with E2EE decryption fan-out. Produces
 * `ChatMessageEntity[]` newest-first (desc by timestamp) and reports whether a
 * subsequent page exists.
 *
 * Flutter source: lib/presentation/screens/chat/chat_screen.dart:398-498.
 *   - Query: `message_nodes/{groupChatId}/messages` ordered by `timestamp`
 *     descending, limited by `_documentLimit`
 *     (= `AppConstants.messagesDetailsPagination` = 30).
 *   - `chat_screen.dart:423-425` — `hasMore = false` once a page returns fewer
 *     docs than the limit. Equivalent: `hasMore = messages.length === limit`.
 *   - Decrypt step — every DTO is routed through the shared
 *     `ChatUtils.shouldDecryptMessageContent` predicate. That method owns the
 *     whole decision: the type list, the share-caption rationale, the Flutter
 *     no-type-gate divergence and every `chat_screen.dart` anchor behind it.
 *   - `chat_screen.dart:492` — post-fetch sort: `b.timestamp.compareTo(a.timestamp)`.
 *
 * Pure-domain contract:
 *   - The use case calls `DecryptTextUseCase` internally — no callback args.
 *     The caller is responsible for having previously invoked
 *     `InitializeTextEncryptionUseCase.execute(peer.publicJwk)` so the
 *     singleton's derived key is bound to the intended peer.
 *   - The use case does NOT touch `firestoreService` directly and does NOT
 *     dispatch to Redux — both are presentation concerns.
 */

import { ChatService } from '@/domain/services/chat/chatService';
import { DecryptTextUseCase, decryptTextUseCase } from '@/domain/usecases/textEncryption/decryptTextUseCase';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import { ChatMessageRemapper } from '@/domain/remappers/chat/chatMessageRemapper';
import { ChatUtils } from '@/domain/utils/chatUtils';

export interface FetchChatMessagesResult {
  messages: ChatMessageEntity[];
  /**
   * The timestamp of the last (oldest, in this desc-ordered page) document in
   * the returned page, or `null` when the page is empty. Pass this back to the
   * next `execute` call as `lastTimestamp` to advance the cursor.
   */
  lastTimestamp: number | null;
  /**
   * `true` when the returned page is the same size as the requested `limit`
   * (so another page may exist). Mirrors `chat_screen.dart:423-425`.
   */
  hasMore: boolean;
}

export class FetchChatMessagesUseCase {
  private chatService: ChatService;
  private decryptTextUseCase: DecryptTextUseCase;

  constructor(
    chatService: ChatService = ChatService.getInstance(),
    decryptUseCase: DecryptTextUseCase = decryptTextUseCase,
  ) {
    this.chatService = chatService;
    this.decryptTextUseCase = decryptUseCase;
  }

  /**
   * Fetch one page of chat messages, decrypt each message's `content`, and
   * return the decoded entities sorted newest-first.
   *
   * @param groupChatId - The chat's group id (sorted-hashCode join of the two user ids).
   * @param lastTimestamp - The timestamp of the last document from the previous
   *   page, or `null` for the first page.
   * @param limit - Page size (typically `SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION = 20`).
   */
  async execute(
    groupChatId: string,
    lastTimestamp: number | null,
    limit: number,
  ): Promise<FetchChatMessagesResult> {
    const { messages: dtos, lastTimestamp: nextLastTimestamp } =
      await this.chatService.fetchMessagesPage(groupChatId, lastTimestamp, limit);

    // Decrypt `content` per DTO and bridge each DTO to its entity via the
    // canonical `ChatMessageRemapper.fromDto`. Decryption is done in parallel
    // because each decrypt is an independent crypto.subtle round-trip; order
    // is preserved by `Promise.all`. Flutter does this serially in a for-loop
    // (`chat_screen.dart:445-476`); doing it in parallel here is a faithful
    // port because the decrypted-content writes are independent and the
    // post-decrypt sort below re-establishes the final order regardless.
    //
    // Decrypt gate — `ChatUtils.shouldDecryptMessageContent`, the SAME
    // predicate `SubscribeToChatUpdatesUseCase` (the live listener) uses. Type
    // list, share-caption rationale and the Flutter no-type-gate divergence are
    // documented on that method.
    const entities: ChatMessageEntity[] = await Promise.all(
      dtos.map(async (dto) => {
        const decryptedContent = ChatUtils.shouldDecryptMessageContent(dto.type, dto.content)
          ? await this.decryptTextUseCase.execute(dto.content!)
          : undefined;
        const entity = ChatMessageRemapper.fromDto(dto);
        if (decryptedContent !== undefined) entity.content = decryptedContent;
        return entity;
      }),
    );

    // Post-fetch sort, desc by timestamp — mirrors chat_screen.dart:492
    // (`b.timestamp.compareTo(a.timestamp)`). Lower index = newer message,
    // matching the reversed VirtualList convention used by `ChatMessagesList`.
    entities.sort((a, b) => b.timestamp - a.timestamp);

    // `hasMore` mirrors chat_screen.dart:423-425 — Flutter sets
    // `_hasMore = false` when the page returned fewer than `_documentLimit`
    // docs; equivalently, `hasMore === true` iff the page is full.
    const hasMore = dtos.length === limit;

    return {
      messages: entities,
      lastTimestamp: nextLastTimestamp,
      hasMore,
    };
  }
}
